/**
 * The public face of feedback: one call plays a cue as sound and as vibration.
 *
 * Fire-and-forget by design — `playCue` never throws, never returns a promise,
 * and never awaits. Callers are render paths and socket callbacks; neither can do
 * anything useful with a failure.
 *
 * IMPORTANT: this layer must NOT read `prefers-reduced-motion`. That setting
 * exists for complaints caused by *visual* motion; a 12 ms tap on the case and a
 * card click are not visual. Coupling them would strip a channel from precisely
 * the user who has just lost the visual one — and since iOS, where Reduce Motion
 * is most often enabled, has no vibrate API at all, the coupling would only ever
 * punish Android. Sound and vibration each have their own toggle.
 */

import type { FeedbackCue } from '@/live/cues';
import { audioNow, withAudio } from '@/lib/audio/context';
import {
  voiceBidExact,
  voiceBidMissed,
  voiceCardPlace,
  voiceDeal,
  voiceGameOver,
  voicePlayerJoined,
  voiceRefused,
  voiceTrickSweep,
  voiceYourTurn,
} from '@/lib/audio/voices';
import { currentSettings, type DeviceSettings } from '@/store/settings';

/**
 * Only the two switches this layer actually reads.
 *
 * Not the whole `DeviceSettings`: the theme lives there too and has nothing to
 * do with whether a card makes a sound. Asking for the narrower thing keeps a
 * new preference from rippling through every caller and every test.
 */
type FeedbackSettings = Pick<DeviceSettings, 'sound' | 'vibration'>;

type Kind = FeedbackCue['kind'];

interface Timing {
  /** Pushed back so cues read as a sequence rather than a smudge. */
  delayMs: number;
  /** Reserved after this cue, so the next one does not overlap it. */
  spacingMs: number;
  /** Below 2 gets dropped when the queue is deep. */
  priority: number;
}

const CUE_TIMING: Record<Kind, Timing> = {
  // "The last card lands, *then* the pile is swept" — without the offset the two
  // arrive together and read as one muddy noise.
  'trick-sweep': { delayMs: 140, spacingMs: 260, priority: 3 },
  'card-place': { delayMs: 0, spacingMs: 40, priority: 1 },
  'your-turn': { delayMs: 60, spacingMs: 200, priority: 3 },
  'bid-exact': { delayMs: 300, spacingMs: 260, priority: 3 },
  'bid-missed': { delayMs: 300, spacingMs: 300, priority: 3 },
  refused: { delayMs: 0, spacingMs: 180, priority: 4 },
  deal: { delayMs: 0, spacingMs: 300, priority: 2 },
  'game-over': { delayMs: 200, spacingMs: 600, priority: 4 },
  'player-joined': { delayMs: 0, spacingMs: 150, priority: 1 },
};

/** Vibration patterns. `deal` is absent on purpose — a burst of ticks as
 *  vibration is just buzzing. */
const VIBRATION: Partial<Record<Kind, number | number[]>> = {
  'card-place': 8,
  'your-turn': 12,
  'trick-sweep': 18,
  'bid-exact': [0, 15, 30, 15],
  'bid-missed': 35,
  refused: [0, 25, 40, 25],
  'game-over': [0, 20, 60, 20, 60, 40],
};

/** How many cues are already scheduled ahead of the clock. */
const MAX_QUEUED_AHEAD_MS = 700;

let nextFree = 0;

export interface GateInput {
  cue: FeedbackCue;
  settings: FeedbackSettings;
  /** Seconds of already-scheduled audio still ahead of now. */
  queuedAheadMs: number;
  hidden: boolean;
}

/**
 * Whether a cue should be heard. Extracted as a pure function because the drop
 * rules are the part worth testing, and because sound-off and vibration-off are
 * two independent axes that are easy to conflate.
 */
export function shouldPlaySound({ cue, settings, queuedAheadMs, hidden }: GateInput): boolean {
  if (!settings.sound) return false;
  if (hidden) return false;
  // A reconnect storm or a very fast table can otherwise build a wall of noise;
  // the low-priority cues are the ones nobody misses.
  if (queuedAheadMs > MAX_QUEUED_AHEAD_MS && CUE_TIMING[cue.kind].priority < 2) return false;
  return true;
}

export function shouldVibrate(
  cue: FeedbackCue,
  settings: FeedbackSettings,
  hidden: boolean
): boolean {
  if (!settings.vibration) return false;
  if (hidden) return false;
  return VIBRATION[cue.kind] !== undefined;
}

function speak(cue: FeedbackCue, at: number): void {
  switch (cue.kind) {
    case 'card-place':
      return voiceCardPlace(at);
    case 'trick-sweep':
      return voiceTrickSweep(at, cue.mine);
    case 'your-turn':
      return voiceYourTurn(at);
    case 'bid-exact':
      return voiceBidExact(at);
    case 'bid-missed':
      return voiceBidMissed(at);
    case 'refused':
      return voiceRefused(at);
    case 'deal':
      return voiceDeal(at, cue.ticks);
    case 'game-over':
      return voiceGameOver(at);
    case 'player-joined':
      return voicePlayerJoined(at);
    default: {
      const exhaustive: never = cue;
      void exhaustive;
    }
  }
}

function isHidden(): boolean {
  return typeof document !== 'undefined' && document.visibilityState === 'hidden';
}

function schedule(cue: FeedbackCue, settings: FeedbackSettings): void {
  const hidden = isHidden();
  const now = audioNow();
  const queuedAheadMs = Math.max(0, (nextFree - now) * 1000);

  if (shouldVibrate(cue, settings, hidden)) {
    // Absent on desktop and on iOS Safari, where it silently no-ops.
    navigator.vibrate?.(VIBRATION[cue.kind] as number | number[]);
  }

  if (!shouldPlaySound({ cue, settings, queuedAheadMs, hidden })) return;

  withAudio(() => {
    const timing = CUE_TIMING[cue.kind];
    // Scheduling on the WebAudio clock, never setTimeout: a backgrounded tab
    // throttles timers to one second and everything would smear.
    const start = Math.max(audioNow() + timing.delayMs / 1000, nextFree);
    nextFree = start + timing.spacingMs / 1000;
    speak(cue, start);
  });
}

/** Play a cue, honouring the device settings. */
export function playCue(cue: FeedbackCue): void {
  schedule(cue, currentSettings());
}

/**
 * Play a cue regardless of the sound setting — for the moment the user switches
 * sound on and needs to hear that it worked.
 */
export function previewCue(cue: FeedbackCue): void {
  schedule(cue, { ...currentSettings(), sound: true });
}

/** Test seam. */
export function resetSchedulerForTests(): void {
  nextFree = 0;
}
